import React, { useState, useEffect } from "react";
import { useFormik } from "formik";
import * as Yup from "yup";
import {
  ThinTextField,
  AddButton,
  ThinDropdownWithSearch,
  BackButton,
} from "../../../components/common/GeneralComponent";
import { fetchData, submitFormWithErrors } from "../../../utils/functions";
import { toast } from "react-toastify";
import { useParams, useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import Loader from "../../../components/partials/Loader";

const EditDeh = () => {
  const { id } = useParams();
  const [loader, setLoader] = useState(true);
  const [districts, setDistricts] = useState([]);
  const [allDistricts, setAllDistricts] = useState([]);
  const [tehsils, setTehsils] = useState([]);
  const [allTehsils, setAllTehsils] = useState([]);
  const [ucs, setUcs] = useState([]);
  const [allUcs, setAllUcs] = useState([]);
  const [filteredDistricts, setFilteredDistricts] = useState([]);
  const [filteredTehsils, setFilteredTehsils] = useState([]);
  const [filteredUcs, setFilteredUcs] = useState([]);
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    const fetchDataForEdit = async () => {
      try {
        // Fetch Deh by ID
        const dehRes = await fetchData(
          "all-dehs",
          "GET",
          null,
          {},
          { deh_id: id },
          navigate,
          "/dehs",
          dispatch
        );
        const dehData = dehRes?.data?.[0];

        if (dehData) {
          formik.setValues({
            deh_id: id,
            deh_name: dehData.deh_name,
            district_id: dehData.district_id,
            tehsil_id: dehData.tehsil_id,
            //uc_id: dehData.uc_id,
            status: dehData.status,
          });

          // Load Districts
          const disRes = await fetchData(
            "districts",
            "GET",
            null,
            {},
            {},
            navigate,
            "/districts",
            dispatch
          );
          const allDists = disRes?.data || [];
          setAllDistricts(allDists);
          setDistricts(allDists);
          setFilteredDistricts(allDists.filter((d) => d.status === 1));

          // Load Tehsils for district
          if (dehData.district_id) {
            const tehRes = await fetchData(
              "tehsils",
              "GET",
              null,
              {},
              { district_id: dehData.district_id },
              navigate,
              "/tehsils",
              dispatch
            );
            const allTehs = tehRes?.data || [];
            setAllTehsils(allTehs);
            setTehsils(allTehs);
            setFilteredTehsils(allTehs.filter((t) => t.status === 1));
          }

          // Load UCs for tehsil
          // if (dehData.tehsil_id) {
          //   const ucRes = await fetchData(
          //     "ucs",
          //     "GET",
          //     null,
          //     {},
          //     { tehsil_id: dehData.tehsil_id },
          //     navigate,
          //     "/ucs",
          //     dispatch
          //   );
          //   const allUcsData = ucRes?.data || [];
          //   setAllUcs(allUcsData);
          //   setUcs(allUcsData);
          //   setFilteredUcs(allUcsData.filter((u) => u.status === 1));
          // }
        } else {
          toast.error("Deh not found");
          navigate("/dehs");
        }
      } catch (error) {
        console.error("Error fetching data:", error);
        toast.error("Failed to load data");
        navigate("/dehs");
      } finally {
        setLoader(false);
      }
    };

    if (id) fetchDataForEdit();
  }, [id, navigate, dispatch]);

  const validationSchema = Yup.object({
    deh_name: Yup.string()
      .required("Deh Name is required")
      .max(100, "Deh name must be 100 characters or less"), 
    district_id: Yup.string().required("District is required"),
    tehsil_id: Yup.string().required("Tehsil is required"),
    //uc_id: Yup.string().required("UC is required"),
    status: Yup.number().required("Status is required"),
  });

  const formik = useFormik({
    initialValues: {
      deh_id: id,
      deh_name: "",
      district_id: "",
      tehsil_id: "",
      //uc_id: "",
      status: 1,
    },
    enableReinitialize: true,
    validationSchema,
    onSubmit: async (values) => {
      setIsSubmitting(true);
      try {
        // Prevent activation if parent inactive
        if (values.status == 1) {
          const selectedDistrict = allDistricts.find(
            (d) =>
              d.district_id == values.district_id ||
              d.District_Id == values.district_id
          );
          if (selectedDistrict && selectedDistrict.status === 0) {
            toast.error(
              "Cannot activate deh. First activate related District."
            );
            setIsSubmitting(false);
            return;
          }

          const selectedTehsil = allTehsils.find(
            (t) =>
              t.tehsil_id == values.tehsil_id || t.Tehsil_Id == values.tehsil_id
          );
          if (selectedTehsil && selectedTehsil.status === 0) {
            toast.error("Cannot activate deh. First activate related Tehsil.");
            setIsSubmitting(false);
            return;
          }

          const selectedUc = allUcs.find((u) => u.uc_id == values.uc_id);
          if (selectedUc && selectedUc.status === 0) {
            toast.error("Cannot activate deh. First activate related UC.");
            setIsSubmitting(false);
            return;
          }
        }

        await fetchData(
          "dehs",
          "PUT",
          values,
          {},
          {},
          navigate,
          "/dehs",
          dispatch
        );
        toast.success("Deh updated successfully");
        navigate("/dehs");
      } catch (error) {
        toast.error(error.message || "Failed to update Deh");
      } finally {
        setIsSubmitting(false);
      }
    },
  });

  const handleDistrictChange = async (selectedValue) => {
    formik.setValues((prev) => ({
      ...prev,
      district_id: selectedValue,
      tehsil_id: "",
      uc_id: "",
    }));

    if (selectedValue) {
      setLoader(true);
      try {
        const res = await fetchData(
          "tehsils",
          "GET",
          null,
          {},
          { district_id: selectedValue },
          navigate,
          "/tehsils",
          dispatch
        );
        const allTehs = res?.data || [];
        setAllTehsils(allTehs);
        setTehsils(allTehs);
        setFilteredTehsils(allTehs.filter((t) => t.status === 1));
        setFilteredUcs([]);
        setAllUcs([]);
      } catch (error) {
        toast.error("Failed to load tehsils");
      } finally {
        setLoader(false);
      }
    }
  };

  // const handleTehsilChange = async (selectedValue) => {
  //   formik.setFieldValue("tehsil_id", selectedValue);
  //   formik.setFieldValue("uc_id", "");

  //   if (selectedValue) {
  //     setLoader(true);
  //     try {
  //       const res = await fetchData(
  //         "ucs",
  //         "GET",
  //         null,
  //         {},
  //         { tehsil_id: selectedValue },
  //         navigate,
  //         "/ucs",
  //         dispatch
  //       );
  //       const allUcsData = res?.data || [];
  //       setAllUcs(allUcsData);
  //       setUcs(allUcsData);
  //       setFilteredUcs(allUcsData.filter((u) => u.status === 1));
  //     } catch (error) {
  //       toast.error("Failed to load UCs");
  //     } finally {
  //       setLoader(false);
  //     }
  //   }
  // };

  if (loader) return <Loader />;

  return (
    <div className="w-full my-4 mx-auto">
      <div className="flex lg:flex-row flex-col lg:justify-between justify-start lg:items-center items-start gap-3 w-full">
        <h1 className="lg:text-[1.475rem] text-[1rem] text-[#212529] font-[Bold]">
          Edit Deh
        </h1>
        <BackButton text="Back" to="/dehs" />
      </div>

      <div className="bg-white rounded-lg shadow w-full lg:mb-[30px] mt-4 mb-[7rem]">
        <div className="p-4 flex md:flex-row rounded-t-lg gradiant2 flex-col gap-3 justify-between md:items-center border-lightGray border-b ">
          <h1 className="lg:text-[0.875rem] text-[13px] tracking-[0.0625rem] text-[#856800] font-[bold] uppercase">
            Edit Deh
          </h1>
        </div>
        <form onSubmit={submitFormWithErrors(formik)} className="p-4">
          <div className="grid grid-cols-2 gap-4 p-4 my-4">
            {/* ❌ Province & Division removed */}
            {/* ❌ Deh Code removed */}

            <ThinDropdownWithSearch
              label="District"
              options={filteredDistricts.map((d) => ({
                value: d.district_id || d.District_Id,
                label: d.district_name || d.District_Name,
              }))}
              value={formik.values.district_id}
              onChange={handleDistrictChange}
              error={formik.touched.district_id && formik.errors.district_id}
              helperText={
                formik.touched.district_id && formik.errors.district_id
              }
            />

            <ThinDropdownWithSearch
              label="Tehsil"
              options={filteredTehsils.map((t) => ({
                value: t.tehsil_id || t.Tehsil_Id,
                label: t.tehsil_name || t.Tehsil_Name,
              }))}
              value={formik.values.tehsil_id}
              onChange={(val) => formik.setFieldValue("tehsil_id", val)}
              disabled={!formik.values.district_id}
              error={formik.touched.tehsil_id && formik.errors.tehsil_id}
              helperText={
                formik.touched.tehsil_id && formik.errors.tehsil_id
              }
            />

            {/* <ThinDropdownWithSearch
              label="UC"
              options={filteredUcs.map((u) => ({
                value: u.uc_id,
                label: u.uc_name,
              }))}
              value={formik.values.uc_id}
              onChange={(val) => formik.setFieldValue("uc_id", val)}
              disabled={!formik.values.tehsil_id}
              error={formik.touched.uc_id && formik.errors.uc_id}
            /> */}

            <ThinTextField
              label="Deh Name"
              name="deh_name"
              value={formik.values.deh_name}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              error={formik.touched.deh_name && formik.errors.deh_name}
              helperText={
                formik.touched.deh_name && formik.errors.deh_name
              }
            />

            <ThinDropdownWithSearch
              label="Status"
              options={[
                { value: 1, label: "Active" },
                { value: 0, label: "In-Active" },
              ]}
              value={formik.values.status}
              onChange={(val) => formik.setFieldValue("status", val)}
              error={formik.touched.status && formik.errors.status}
            />
          </div>

          <div className="p-4">
            <AddButton
              text="Submit"
              type="Submit"
              isLoading={isSubmitting}
              disabled={isSubmitting}
            />
          </div>
        </form>
      </div>
    </div>
  );
};

export default EditDeh;
